import { useEffect } from "react";
import { useSeoOverride, useSite } from "@/context/SiteDataContext";

/**
 * Per-page metadata manager.
 *
 * Pages pass sensible defaults as props. Anything configured for the same
 * `routeKey` under "SEO pages" in the CMS wins, so the client can retitle a
 * page, swap its social image or no-index it without a deploy.
 *
 * Precedence: CMS override > page props > site-wide default.
 */
const setMeta = (attr, key, value) => {
  const existing = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!value) {
    if (existing) existing.remove();
    return;
  }
  const el = existing || document.createElement("meta");
  if (!existing) {
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const setCanonical = (href) => {
  const existing = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (existing) existing.remove();
    return;
  }
  const el = existing || document.createElement("link");
  if (!existing) {
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export default function Seo({
  routeKey,
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  keywords,
  robots,
  jsonLd,
}) {
  const override = useSeoOverride(routeKey);
  const site = useSite();

  // Pull the individual values out so the effect does not re-run on every
  // render just because the override object identity changed.
  const finalTitle = override?.title || title;
  const finalDescription = override?.description || description;
  const finalCanonical = override?.canonical || canonical;
  const finalOgTitle = override?.ogTitle || ogTitle;
  const finalOgDescription = override?.ogDescription || ogDescription;
  // og:image has to be an absolute URL. A scraper reads the tag out of the
  // page with no browsing context to resolve "/img/..." against, so a relative
  // value means no preview image at all — it fails silently and only shows up
  // when someone shares a link. The prerendered HTML already absolutises these;
  // this keeps the runtime tags in step.
  const absolute = (url) => {
    if (!url) return url;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
  };
  const finalOgImage = absolute(override?.ogImage || ogImage);
  const finalKeywords = override?.keywords || keywords;
  const finalRobots = override?.robots || robots;
  const siteName = site?.shortName || "Athena Scientific";
  const tagline = site?.tagline || "Driven by Science. Focused on You.";
  const overrideJsonLd = override?.jsonLd;

  useEffect(() => {
    const full = finalTitle ? `${finalTitle} | ${siteName}` : `${siteName} — ${tagline}`;
    document.title = full;

    setMeta("name", "description", finalDescription);
    setMeta("name", "keywords", finalKeywords);
    setMeta("name", "robots", finalRobots);

    setMeta("property", "og:site_name", siteName);
    setMeta("property", "og:title", finalOgTitle || full);
    setMeta("property", "og:description", finalOgDescription || finalDescription);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", finalCanonical);
    setMeta("property", "og:image", finalOgImage);

    setMeta("name", "twitter:card", finalOgImage ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", finalOgTitle || full);
    setMeta("name", "twitter:description", finalOgDescription || finalDescription);
    setMeta("name", "twitter:image", finalOgImage);

    setCanonical(finalCanonical);
  }, [
    finalTitle,
    finalDescription,
    finalCanonical,
    finalOgTitle,
    finalOgDescription,
    finalOgImage,
    finalKeywords,
    finalRobots,
    siteName,
    tagline,
  ]);

  // Structured data is a separate effect so it can be cleanly torn down — a
  // stale JSON-LD block describing the previous page is worse than none.
  useEffect(() => {
    const data = overrideJsonLd || jsonLd;
    if (!data) return undefined;

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = typeof data === "string" ? data : JSON.stringify(data);
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [jsonLd, overrideJsonLd]);

  return null;
}
